import { fetchLastStates } from '@/services/states-api.service'
import { getBoardMeta } from '@/services/boards-api.service.js'
import { logger } from '@/utils/logger'

/**
 * State types that indicate Dayforce sync is complete.
 * Sync is considered complete when all these state types have `finishedAt` set.
 */
const DAYFORCE_SYNC_STATE_TYPES = ['sync', 'sync_people', 'fetch_dayforce_data']

const STORAGE_KEY = 'dayforce-sync-boardId'
const DAYFORCE_SYNC_META_KEY = 'dayforce_sync_status'
const DEFAULT_POLL_INTERVAL = 3000
const DAYFORCE_SYNC_LOCK_TTL_MS = 3 * 60 * 60 * 1000
const SYNCING_MESSAGE =
  'Syncing board. This may take up to 3 hours. Some functionality may be disabled.'
export const SYNC_FAILED_MESSAGE = 'Dayforce data sync failed. Please try again.'

/**
 * Check if all required sync states have completed
 */
function checkSyncComplete(statesList) {
  if (!statesList || statesList.length === 0) {
    return false
  }

  const statesByType = {}
  for (const state of statesList) {
    statesByType[state.type] = state
  }

  return DAYFORCE_SYNC_STATE_TYPES.every((type) => {
    const state = statesByType[type]
    return state && state.finishedAt != null
  })
}

/**
 * Check if sync is in progress (states exist but not all finished)
 */
function checkSyncInProgress(statesList) {
  if (!statesList || statesList.length === 0) {
    return false
  }

  // Sync is in progress if at least one state exists with startedAt but not all have finishedAt
  const hasStarted = statesList.some((s) => s.startedAt != null)
  const allFinished = checkSyncComplete(statesList)

  return hasStarted && !allFinished
}

/**
 * Return the backend timestamp that anchors the sync timeout, if available.
 *
 * @param {Array<{type: string, startedAt?: string}>} statesList - Sync state records.
 * @returns {number|null} The `sync` state's start time in milliseconds, or null when unavailable.
 */
function getSyncStartedAtMs(statesList) {
  const syncStartedAt = statesList?.find((state) => state.type === 'sync')?.startedAt
  if (!syncStartedAt) {
    return null
  }

  const startedAtMs = new Date(syncStartedAt).getTime()
  return Number.isNaN(startedAtMs) ? null : startedAtMs
}

/**
 * Wait for the next polling interval or resolve early when polling is aborted.
 *
 * @param {number} delayMs - Maximum delay before the next poll.
 * @param {AbortSignal|undefined} signal - Optional polling cancellation signal.
 * @returns {Promise<void>}
 */
function waitForNextPoll(delayMs, signal) {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(complete, delayMs)

    function complete() {
      clearTimeout(timeoutId)
      signal?.removeEventListener('abort', complete)
      resolve()
    }

    if (signal?.aborted) {
      complete()
    } else {
      signal?.addEventListener('abort', complete, { once: true })
    }
  })
}

/**
 * Save boardId to localStorage for sync recovery after page refresh
 */
export function saveSyncBoardId(boardId) {
  try {
    localStorage.setItem(STORAGE_KEY, boardId)
  } catch (e) {
    logger.warn('[Dayforce Sync] Failed to save boardId to localStorage:', e.message)
  }
}

/**
 * Get saved boardId from localStorage
 */
export function getSavedSyncBoardId() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch (e) {
    return null
  }
}

/**
 * Clear saved boardId from localStorage
 */
export function clearSyncBoardId() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

/**
 * Initial Dayforce syncs have no existing data to display, so they use the
 * blocking sync view. Refreshes keep the existing board accessible.
 *
 * @param {{isInProgress: boolean, error?: string|null, lastUploadDate?: string|null}} syncState - Current sync state.
 * @returns {boolean} Whether navigation should remain on the blocking initial-sync view.
 */
export function shouldShowEmbedNoBoardsForSync({ isInProgress, error, lastUploadDate }) {
  return Boolean((isInProgress || error) && !lastUploadDate)
}

/**
 * Build the user-facing message for the current board operation.
 *
 * @param {string} stateAction - Current board state action.
 * @returns {string} The status message, or an empty string when no action is active.
 */
export function getBoardStateMessage(stateAction) {
  if (!stateAction) {
    return ''
  }

  if (stateAction === 'Syncing') {
    return SYNCING_MESSAGE
  }

  return `${stateAction} board, some functionality may be disabled`
}

/**
 * Check current sync status for a board
 * @returns {{ isComplete: boolean, isInProgress: boolean, error: string|null }}
 */
export async function checkSyncStatus(boardId) {
  try {
    const states = await fetchLastStates({ boardId })
    const isComplete = checkSyncComplete(states)
    const isInProgress = checkSyncInProgress(states)

    if (!isComplete) {
      return { isComplete, isInProgress, error: null }
    }

    const status = (await getBoardMeta({ boardId, key: DAYFORCE_SYNC_META_KEY }))?.data
    if (status?.phase === 'running') {
      return { isComplete: false, isInProgress: true, error: null }
    }
    if (status?.phase === 'failed') {
      return {
        isComplete: false,
        isInProgress: false,
        error: status.error || SYNC_FAILED_MESSAGE
      }
    }

    return { isComplete, isInProgress, error: null }
  } catch (err) {
    logger.error('[Dayforce Sync] Error checking sync status:', err.message)
    return { isComplete: false, isInProgress: false, error: null }
  }
}

/**
 * Wait for Dayforce sync to complete by polling backend states.
 *
 * @param {string} boardId - The board ID to check states for
 * @param {Object} options
 * @param {number} options.pollInterval - Polling interval in ms (default: 3000)
 * @param {number} options.timeoutMs - Maximum time since backend sync start (default: 10800000)
 * @param {string} options.processId - Optional processId to scope state queries to this run.
 *   When provided, polling ignores any prior sync's states (avoids a race where stale
 *   completed states are read before the new run writes its rows).
 * @param {AbortSignal} options.signal - Stops polling without reporting a timeout.
 * @returns {Promise<boolean>} Resolves to true on success, false on timeout or cancellation.
 * @throws {Error} When the matching backend sync outcome is failed.
 */
export async function waitForDayforceSync(boardId, options = {}) {
  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    timeoutMs = DAYFORCE_SYNC_LOCK_TTL_MS,
    processId = null,
    signal
  } = options
  let deadlineMs = Date.now() + timeoutMs
  let hasBackendDeadline = false
  let failedSyncError = null

  while (Date.now() < deadlineMs) {
    if (signal?.aborted) {
      return false
    }

    try {
      const states = await fetchLastStates({ boardId, processId, signal })
      if (!hasBackendDeadline) {
        const syncStartedAtMs = getSyncStartedAtMs(states)
        if (syncStartedAtMs != null) {
          deadlineMs = syncStartedAtMs + timeoutMs
          hasBackendDeadline = true
        }
      }

      if (checkSyncComplete(states)) {
        const status = (await getBoardMeta({ boardId, key: DAYFORCE_SYNC_META_KEY }))?.data
        const isMatchingAttempt = !processId || status?.processId === processId
        const hasMatchingStatus = Boolean(status && isMatchingAttempt)
        const isWaitingForMatchingStatus = Boolean(processId && !hasMatchingStatus)

        if (hasMatchingStatus && status.phase === 'failed') {
          clearSyncBoardId()
          failedSyncError = new Error(status.error || SYNC_FAILED_MESSAGE)
          break
        }

        if (!isWaitingForMatchingStatus && (!hasMatchingStatus || status.phase === 'done')) {
          clearSyncBoardId()
          return true
        }
      }
    } catch (err) {
      logger.error('[Dayforce Sync] Error fetching states:', err.message)
      // Continue polling even on error, might be temporary network issue
    }

    if (signal?.aborted) {
      return false
    }

    const remainingMs = deadlineMs - Date.now()
    if (remainingMs > 0) {
      await waitForNextPoll(Math.min(pollInterval, remainingMs), signal)
    }
  }

  if (failedSyncError) {
    throw failedSyncError
  }

  if (signal?.aborted) {
    return false
  }

  logger.warn(`[Dayforce Sync] Polling timed out for board ${boardId} after ${timeoutMs}ms`)
  return false
}
